import React, {forwardRef, useEffect, useRef} from "react";
import {AgGridReact} from "ag-grid-react";
import Card from "../../../components/card";
import {formatSalary, parseSalary} from "../../../utils/mask";
import "./candidate-sheet.css";

const emptyValue = "Não informado";

const getOptionLabel = (value) => {
    if (!value) return emptyValue;
    if (typeof value === "object") return value.label ?? value.name ?? emptyValue;
    return String(value);
};

const formatDate = (value) => {
    if (!value) return emptyValue;

    const date = value instanceof Date ? value : new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return emptyValue;

    return new Intl.DateTimeFormat("pt-BR").format(date);
};

const formatBoolean = (value) => value ? "Sim" : "Não";

const parseMoney = (value) => {
    if (typeof value === "number") return value;

    const normalized = String(value ?? "").trim();
    if (/^\d+(\.\d{1,2})?$/.test(normalized)) return Number(normalized);

    return parseSalary(normalized);
};

const formatMoney = (value) => value ? formatSalary(parseMoney(value)) : emptyValue;

const hasRichTextContent = (value) => String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim().length > 0;

const InformationItem = ({label, value, size}) => {
    const hasValue = value !== null && value !== undefined && value !== "" && value !== emptyValue;

    return (
        <div className={`candidate-sheet__item${size ? ` candidate-sheet__item--${size}` : ""}`}>
            <span className="candidate-sheet__label">{label}{label.endsWith("?") ? "" : ":"}</span>
            <span className={`candidate-sheet__value${hasValue ? "" : " candidate-sheet__value--empty"}`}>
                {hasValue ? value : emptyValue}
            </span>
        </div>
    );
};

const Section = ({title, children}) => (
    <section className="candidate-sheet__section">
        <h2 className="candidate-sheet__section-title">{title}</h2>
        {children}
    </section>
);

const benefitColumnDefs = [
    {
        field: "benefit_name",
        headerName: "Benefício",
        flex: 2,
        minWidth: 0,
    },
    {
        field: "monthly_value",
        headerName: "Valor mensal",
        flex: 1,
        minWidth: 0,
        valueFormatter: ({value}) => formatMoney(value),
    },
];

const benefitDefaultColDef = {
    editable: false,
    resizable: false,
    sortable: false,
};

const CandidateSheet = forwardRef(({candidate, candidateId, printable = true}, ref) => {
    const benefitsGridRef = useRef(null);
    const benefits = candidate.benefits ?? [];
    const qualifications = candidate.qualifications ?? [];
    const benefitsTotal = benefits.reduce(
        (total, benefit) => total + parseMoney(benefit.monthly_value),
        0,
    );
    const estimatedRevenue = parseMoney(candidate.last_salary) + benefitsTotal;

    useEffect(() => {
        const fitBenefitsColumns = () => benefitsGridRef.current?.api?.sizeColumnsToFit();

        window.addEventListener("beforeprint", fitBenefitsColumns);
        window.addEventListener("afterprint", fitBenefitsColumns);

        return () => {
            window.removeEventListener("beforeprint", fitBenefitsColumns);
            window.removeEventListener("afterprint", fitBenefitsColumns);
        };
    }, []);

    return (
        <div ref={ref}
             className={`candidate-print-area${printable ? " candidate-print-area--printable" : ""}`}>
            <Card wrapperClass="candidate-print-card" bodyClass="candidate-print-card__body">
                <article className="candidate-sheet">
                    <header className="candidate-sheet__header">
                        <h1>Ficha do candidato</h1>
                        <p className="candidate-sheet__candidate-name">
                            {candidate.name || "Candidato sem nome"}
                        </p>
                    </header>

                    <Section title="Dados pessoais">
                        <div className="candidate-sheet__grid">
                            <InformationItem size="third" label="CPF" value={candidate.cpf}/>
                            <InformationItem size="third" label="RG" value={candidate.rg}/>
                            <InformationItem size="third" label="CIN" value={candidate.cin}/>
                            <InformationItem size="third" label="Idade" value={candidate.age}/>
                            <InformationItem size="third" label="Gênero" value={getOptionLabel(candidate.gender)}/>
                            <InformationItem size="third" label="Cidade" value={candidate.city}/>
                            <InformationItem size="third" label="Indicação" value={candidate.referral}/>
                            <InformationItem size="two-thirds" label="Formação" value={candidate.formation}/>
                            <InformationItem size="two-thirds" label="CNH" value={candidate.cnh ? "Sim" : "Não"}/>
                            <InformationItem size="third" label="Categoria" value={candidate.cnh_category}/>
                        </div>
                    </Section>

                    <Section title="Dados profissionais">
                        <div className="candidate-sheet__grid">
                            <InformationItem size="two-thirds" label="Departamento de interesse" value={getOptionLabel(candidate.department_id)}/>
                            <InformationItem size="third" label="Cargo de interesse" value={getOptionLabel(candidate.position_id)}/>
                            <InformationItem size="third" label="Está empregado?" value={formatBoolean(candidate.employed)}/>
                            <InformationItem size="third" label="Regime de contratação" value={getOptionLabel(candidate.employment_regime)}/>
                            <InformationItem size="third" label="Último salário" value={formatMoney(candidate.last_salary)}/>
                            <InformationItem size="third" label="Pretensão salarial" value={formatMoney(candidate.salary_expectation)}/>
                            <InformationItem size="third" label="Último emprego" value={candidate.last_employer}/>
                            <InformationItem size="third" label="Avaliação" value={candidate.evaluation}/>
                            <InformationItem size="third" label="Data da avaliação" value={formatDate(candidate.evaluation_date)}/>
                        </div>
                    </Section>

                    <Section title="Capacitações">
                        {qualifications.length ? (
                            <div className="candidate-sheet__qualifications">
                                {qualifications.map((qualification, index) => (
                                    <InformationItem label={`Capacitação ${index + 1}`} value={getOptionLabel(qualification)}
                                                     key={qualification.value ?? qualification.id ?? index}/>
                                ))}
                            </div>
                        ) : <p className="candidate-sheet__empty">Nenhuma capacitação cadastrada.</p>}
                    </Section>

                    <Section title="Benefícios e adicionais">
                        <div className="ag-theme-quartz candidate-sheet__benefits-grid">
                            <AgGridReact
                                ref={benefitsGridRef}
                                rowData={benefits}
                                columnDefs={benefitColumnDefs}
                                defaultColDef={benefitDefaultColDef}
                                domLayout="autoHeight"
                                headerHeight={32}
                                rowHeight={30}
                                animateRows={false}
                                suppressCellFocus
                                suppressRowClickSelection
                                onGridReady={({api}) => api.sizeColumnsToFit()}
                                overlayNoRowsTemplate="<span>Nenhum benefício cadastrado.</span>"
                            />
                        </div>
                    </Section>

                    <Section title="Observações">
                        {hasRichTextContent(candidate.observation) ? (
                            <div
                                className="candidate-sheet__observation"
                                dangerouslySetInnerHTML={{__html: candidate.observation}}
                            />
                        ) : (
                            <p className="candidate-sheet__observation candidate-sheet__empty">{emptyValue}</p>
                        )}
                    </Section>

                    <footer className="candidate-sheet__footer">
                        <span>Total estimado de ganhos</span>
                        <strong>{formatSalary(estimatedRevenue)}</strong>
                    </footer>
                </article>
            </Card>
        </div>
    );
});

CandidateSheet.displayName = "CandidateSheet";

export default CandidateSheet;
